import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { batch } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Switch,
    Typography
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import { selectSideMenuItem, toogleAppIsLoading } from 'store/slices/main';
import { useDispatch, useSelector } from 'store';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import Avatar from 'ui-component/extended/Avatar';

// project hooks
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';

// assets
import PersonIcon from '@mui/icons-material/Person';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutTwoToneIcon from '@mui/icons-material/LogoutTwoTone';
import ManageAccountsTwoToneIcon from '@mui/icons-material/ManageAccountsTwoTone';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const navigate = useNavigate();
    const { borderRadius, navType, rtlLayout, dndMode, setConfigByName } = useConfig();
    const { logout, user, updateProfileSetting } = useAuth();

    const { appIsLoading } = useSelector((state) => state.main);

    const [open, setOpen] = useState(false);
    const currHour = new Date().getHours();

    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     */
    const anchorRef = useRef(null);
    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': { stroke: theme.palette.primary.light }
                    },
                    '& .MuiChip-label': { lineHeight: 0 }
                }}
                icon={
                    user?.avatar ? (
                        <Avatar
                            alt={`${user?.name} ${user?.lastName}`}
                            avatar={user.avatar}
                            sx={{
                                ...theme.typography.mediumAvatar,
                                margin: '8px 0 8px 8px !important',
                                cursor: 'pointer'
                            }}
                            ref={anchorRef}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            color="inherit"
                        />
                    ) : (
                        <Avatar
                            alt={`${user?.name} ${user?.lastName}`}
                            sx={{
                                ...theme.typography.mediumAvatar,
                                margin: '8px 0 8px 8px !important',
                                cursor: 'pointer'
                            }}
                            ref={anchorRef}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            color="inherit"
                        >
                            <PersonIcon />
                        </Avatar>
                    )
                }
                label={<SettingsOutlinedIcon color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={() => setOpen((prevOpen) => !prevOpen)}
                color="primary"
            />
            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 14] } }] }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={(event) => {
                            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                                return;
                            }

                            setOpen(false);
                        }}
                    >
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Typography variant="h4">
                                                {(currHour > 4 && currHour <= 12 && (
                                                    <FormattedMessage id="ui-comp-profile-goodmorning" values={{ username: user?.name }} />
                                                )) ||
                                                    (currHour > 12 && currHour <= 19 && (
                                                        <FormattedMessage
                                                            id="ui-comp-profile-goodafternoon"
                                                            values={{ username: user?.name }}
                                                        />
                                                    )) ||
                                                    ((currHour > 19 || currHour <= 4) && (
                                                        <FormattedMessage
                                                            id="ui-comp-profile-goodevening"
                                                            values={{ username: user?.name }}
                                                        />
                                                    ))}
                                            </Typography>
                                            <Divider />
                                        </Box>
                                        <Box sx={{ p: 2, pt: 0 }}>
                                            <Card
                                                sx={{
                                                    bgcolor:
                                                        theme.palette.mode === 'dark'
                                                            ? theme.palette.dark[800]
                                                            : theme.palette.primary.light,
                                                    my: 2
                                                }}
                                            >
                                                <CardContent>
                                                    <Grid container spacing={3} direction="column">
                                                        <Grid item>
                                                            <Grid item container alignItems="center" justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="subtitle1">
                                                                        <FormattedMessage id="ui-comp-profile-darkmode" />
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Switch
                                                                        color="primary"
                                                                        checked={navType === 'dark'}
                                                                        disabled={appIsLoading}
                                                                        onChange={(e) => {
                                                                            dispatch(toogleAppIsLoading(true));
                                                                            updateProfileSetting(
                                                                                'navType',
                                                                                {
                                                                                    fieldDataType: 'string',
                                                                                    fieldValue: e.target.checked ? 'dark' : 'light'
                                                                                },
                                                                                () =>
                                                                                    batch(() => {
                                                                                        setConfigByName(
                                                                                            'navType',
                                                                                            e.target.checked ? 'dark' : 'light'
                                                                                        );
                                                                                        dispatch(toogleAppIsLoading(false));
                                                                                        window.location.reload(true);
                                                                                    })
                                                                            );
                                                                        }}
                                                                        name="darkMode"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item>
                                                            <Grid item container alignItems="center" justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="subtitle1">
                                                                        <FormattedMessage id="ui-comp-profile-rtlmode" />
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Switch
                                                                        color="primary"
                                                                        checked={rtlLayout}
                                                                        disabled={appIsLoading}
                                                                        onChange={(e) => {
                                                                            dispatch(toogleAppIsLoading(true));
                                                                            updateProfileSetting(
                                                                                'rtlLayout',
                                                                                {
                                                                                    fieldDataType: 'boolean',
                                                                                    fieldValue: e.target.checked.toString()
                                                                                },
                                                                                () =>
                                                                                    batch(() => {
                                                                                        setConfigByName('rtlLayout', e.target.checked);
                                                                                        dispatch(toogleAppIsLoading(false));
                                                                                        window.location.reload(true);
                                                                                    })
                                                                            );
                                                                        }}
                                                                        name="rtl"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item>
                                                            <Grid item container alignItems="center" justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="subtitle1">
                                                                        <FormattedMessage id="ui-comp-profile-dndmode" />
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Switch
                                                                        checked={dndMode}
                                                                        disabled={appIsLoading}
                                                                        onChange={(e) => {
                                                                            dispatch(toogleAppIsLoading(true));
                                                                            updateProfileSetting(
                                                                                'dndMode',
                                                                                {
                                                                                    fieldDataType: 'boolean',
                                                                                    fieldValue: e.target.checked.toString()
                                                                                },
                                                                                () =>
                                                                                    batch(() => {
                                                                                        setConfigByName('dndMode', e.target.checked);
                                                                                        dispatch(toogleAppIsLoading(false));
                                                                                        window.location.reload(true);
                                                                                    })
                                                                            );
                                                                        }}
                                                                        name="dndMode"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                            <Divider />
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: { minWidth: '100%' },
                                                    '& .MuiListItemButton-root': { mt: 0.5 }
                                                }}
                                            >
                                                <ListItemButton
                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                    disabled={appIsLoading}
                                                    onClick={(event) =>
                                                        batch(() => {
                                                            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                                                                return;
                                                            }

                                                            setOpen(false);
                                                            dispatch(selectSideMenuItem(null));
                                                            navigate('/profile');
                                                        })
                                                    }
                                                >
                                                    <ListItemIcon>
                                                        <ManageAccountsTwoToneIcon size="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2">
                                                                <FormattedMessage id="ui-comp-profile-profilesettings" />
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                    disabled={appIsLoading}
                                                    onClick={() => {
                                                        dispatch(toogleAppIsLoading(true));
                                                        logout(() => dispatch(toogleAppIsLoading(false)));
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <LogoutTwoToneIcon size="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2">
                                                                <FormattedMessage id="ui-comp-profile-logout" />
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
